
// Regular expression to match template variables in the format {{ variable.name }}
const VARIABLE_REGEX = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

// Import the central variables list
import { getVariableNames } from './templateVariables';

/**
 * Extract all template variables from a string
 */
export const extractVariables = (content: string): string[] => {
  const variables: string[] = [];
  let match;
  
  while ((match = VARIABLE_REGEX.exec(content)) !== null) {
    if (match[1]) {
      variables.push(match[1]);
    }
  }
  
  return [...new Set(variables)]; // Remove duplicates
};

/**
 * Validate if a variable is in the allowed variables list
 */
export const isValidVariable = (variable: string): boolean => {
  return getVariableNames().includes(variable);
};

/**
 * Find invalid variables in a list of variables
 */
export const findInvalidVariables = (variables: string[]): string[] => {
  return variables.filter(variable => !isValidVariable(variable));
};

/**
 * Check if a template string contains invalid variables
 */
export const hasInvalidVariables = (content: string): boolean => {
  const variables = extractVariables(content);
  return findInvalidVariables(variables).length > 0;
};

/**
 * Get all invalid variables from a template string
 */
export const getInvalidVariables = (content: string): string[] => {
  const variables = extractVariables(content);
  return findInvalidVariables(variables);
};

/**
 * Highlight template variables in HTML content
 * Returns HTML with variables wrapped in span elements with appropriate classes
 */
export const highlightVariables = (content: string): string => {
  if (!content) return '';
  
  // Replace variables with highlighted spans
  return content.replace(
    VARIABLE_REGEX,
    (match, variable) => {
      const isValid = isValidVariable(variable);
      const className = isValid ? 'template-variable-valid' : 'template-variable-invalid';
      return `<span class="${className}" data-variable="${variable}">${match}</span>`;
    }
  );
};
